import React, { useState, useEffect } from 'react';
import { FileText, Upload, Trash2, Download, Plus, Edit, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { supabase } from '../../../lib/supabaseClient';

const PDFTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Dialog states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    file: null,
    setAsDefault: true,  // Default to true for new uploads
    template_format: 'html',  // 'html' or 'pdf'
    template_content: ''  // HTML content for HTML templates
  });
  const [uploading, setUploading] = useState(false);

  // Available placeholders for job listings
  const AVAILABLE_PLACEHOLDERS = [
    { placeholder: '{{job_title}}', description: 'Job title', field: 'title' },
    { placeholder: '{{ref_code}}', description: 'Reference code', field: 'ref_code' },
    { placeholder: '{{client_name}}', description: 'Client company name', field: 'client.name' },
    { placeholder: '{{clearance}}', description: 'Security clearance required', field: 'clearance_required' },
    { placeholder: '{{locations}}', description: 'Work locations', field: 'locations' },
    { placeholder: '{{work_arrangement}}', description: 'Work arrangement', field: 'work_arrangement' },
    { placeholder: '{{close_date}}', description: 'Closing date', field: 'client_close_datetime' },
    { placeholder: '{{start_date}}', description: 'Estimated start date', field: 'estimated_start_date' },
    { placeholder: '{{term}}', description: 'Initial term (months)', field: 'initial_term_months' },
    { placeholder: '{{extension}}', description: 'Extension options', field: 'extension_options' },
    { placeholder: '{{description}}', description: 'Position description', field: 'description' },
    { placeholder: '{{criteria}}', description: 'Selection criteria', field: 'selection_criteria' },
  ];

  // Default HTML template - matches /job/ page design
  const DEFAULT_HTML_TEMPLATE = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>{{job_title}}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: #0f172a;
      background: #ffffff;
      padding: 32px 24px;
      max-width: 896px;
      margin: 0 auto;
    }

    /* Job Header */
    .job-header {
      margin-bottom: 24px;
    }

    h1 {
      font-size: 36px;
      font-weight: 700;
      margin-bottom: 8px;
      color: #0f172a;
      line-height: 1.2;
    }

    .header-meta {
      display: flex;
      gap: 24px;
      color: #64748b;
      font-size: 14px;
      margin-bottom: 16px;
    }

    .header-meta-item {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    /* Alert Box */
    .alert {
      background: #f0f9ff;
      border: 1px solid #bae6fd;
      border-radius: 6px;
      padding: 12px 16px;
      margin-bottom: 24px;
      display: flex;
      align-items: start;
      gap: 12px;
    }

    .alert strong {
      font-weight: 600;
      color: #0c4a6e;
    }

    /* Card Styles */
    .card {
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      margin-bottom: 24px;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    }

    .card-header {
      padding: 20px 24px;
      border-bottom: 1px solid #e2e8f0;
    }

    .card-title {
      font-size: 18px;
      font-weight: 600;
      color: #0f172a;
    }

    .card-content {
      padding: 24px;
    }

    /* Position Details Grid */
    .details-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 24px;
    }

    .detail-item {
      display: flex;
      align-items: start;
      gap: 12px;
    }

    .detail-label {
      font-size: 14px;
      font-weight: 500;
      color: #64748b;
      margin-bottom: 4px;
    }

    .detail-value {
      font-weight: 500;
      color: #0f172a;
    }

    .badge {
      display: inline-block;
      padding: 4px 12px;
      background: #f1f5f9;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      font-size: 14px;
      font-weight: 500;
      color: #475569;
      margin-top: 4px;
    }

    /* Content Sections */
    .prose {
      color: #334155;
      font-size: 14px;
      line-height: 1.75;
    }

    .prose p {
      margin-bottom: 16px;
    }

    .prose ul {
      list-style-type: disc;
      margin-left: 24px;
      margin-bottom: 16px;
    }

    .prose ol {
      list-style-type: decimal;
      margin-left: 24px;
      margin-bottom: 16px;
    }

    .prose li {
      margin-bottom: 8px;
    }

    .prose h1 {
      font-size: 24px;
      font-weight: 700;
      margin-top: 24px;
      margin-bottom: 16px;
    }

    .prose h2 {
      font-size: 20px;
      font-weight: 700;
      margin-top: 20px;
      margin-bottom: 12px;
    }

    .prose h3 {
      font-size: 18px;
      font-weight: 600;
      margin-top: 16px;
      margin-bottom: 8px;
    }

    .prose strong {
      font-weight: 600;
      color: #0f172a;
    }

    /* About Section Subsections */
    .subsection {
      margin-bottom: 16px;
    }

    .subsection:last-child {
      margin-bottom: 0;
    }

    .subsection h3 {
      font-size: 14px;
      font-weight: 500;
      color: #0f172a;
      margin-bottom: 8px;
    }

    .subsection-content {
      color: #64748b;
      font-size: 14px;
      line-height: 1.75;
    }

    /* Footer */
    .footer {
      margin-top: 48px;
      padding-top: 24px;
      border-top: 1px solid #e2e8f0;
      text-align: center;
      color: #64748b;
      font-size: 14px;
    }

    /* Icon replacements (using symbols) */
    .icon {
      width: 20px;
      height: 20px;
      display: inline-block;
      color: #64748b;
      font-size: 16px;
    }

    @media print {
      body {
        padding: 16px;
      }
      .card {
        box-shadow: none;
        page-break-inside: avoid;
      }
    }
  </style>
</head>
<body>
  <!-- Job Header -->
  <div class="job-header">
    <h1>{{job_title}}</h1>
    <div class="header-meta">
      <div class="header-meta-item">
        <span class="icon">🏢</span>
        <span>{{client_name}}</span>
      </div>
      <div class="header-meta-item">
        <span class="icon">💼</span>
        <span>{{ref_code}}</span>
      </div>
    </div>

    <div class="alert">
      <span class="icon">📅</span>
      <div>
        <strong>Applications close:</strong> {{close_date}}
      </div>
    </div>
  </div>

  <!-- Position Details -->
  <div class="card">
    <div class="card-header">
      <h2 class="card-title">Position Details</h2>
    </div>
    <div class="card-content">
      <div class="details-grid">
        <div class="detail-item">
          <span class="icon">🏠</span>
          <div>
            <div class="detail-label">Work Arrangement</div>
            <div class="detail-value">{{work_arrangement}}</div>
          </div>
        </div>

        <div class="detail-item">
          <span class="icon">📍</span>
          <div>
            <div class="detail-label">Location</div>
            <div class="detail-value">{{locations}}</div>
          </div>
        </div>

        <div class="detail-item">
          <span class="icon">🛡️</span>
          <div>
            <div class="detail-label">Security Clearance Required</div>
            <div class="badge">{{clearance}}</div>
          </div>
        </div>

        <div class="detail-item">
          <span class="icon">📅</span>
          <div>
            <div class="detail-label">Estimated Start Date</div>
            <div class="detail-value">{{start_date}}</div>
          </div>
        </div>

        <div class="detail-item">
          <span class="icon">⏱️</span>
          <div>
            <div class="detail-label">Contract Duration</div>
            <div class="detail-value">{{term}}</div>
            <div class="detail-label" style="margin-top: 4px; font-size: 12px;">Extensions: {{extension}}</div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Description -->
  <div class="card">
    <div class="card-header">
      <h2 class="card-title">About the Role</h2>
    </div>
    <div class="card-content">
      <div class="prose">{{description}}</div>
    </div>
  </div>

  <!-- Selection Criteria -->
  <div class="card">
    <div class="card-header">
      <h2 class="card-title">Selection Criteria</h2>
    </div>
    <div class="card-content">
      <div class="prose">{{criteria}}</div>
    </div>
  </div>

  <!-- Footer -->
  <div class="footer">
    <p>© ${new Date().getFullYear()} Appsytec. All rights reserved.</p>
  </div>
</body>
</html>`;

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('pdf_templates')
        .select('*')
        .eq('template_type', 'job_listing')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTemplates(data || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (file.type !== 'application/pdf') {
        setError('Please upload a PDF file');
        return;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setFormData({ ...formData, file });
      setError(null);
    }
  };

  const handleUpload = async () => {
    // Validate based on format
    if (!formData.name) {
      setError('Please provide a template name');
      return;
    }

    if (formData.template_format === 'pdf' && !formData.file) {
      setError('Please select a PDF file');
      return;
    }

    if (formData.template_format === 'html' && !formData.template_content.trim()) {
      setError('Please provide HTML content');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      // Get current user
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      let filePath = null;
      let fileName = null;
      let fileSize = null;

      // Only upload file if PDF format
      if (formData.template_format === 'pdf') {
        // Upload file to Supabase Storage
        fileName = `${Date.now()}_${formData.file.name}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('pdf-templates')
          .upload(fileName, formData.file);

        if (uploadError) throw uploadError;

        // For private buckets, store just the file path (not public URL)
        // Backend will use Supabase client to download with proper auth
        filePath = `pdf-templates/${fileName}`;
        fileSize = formData.file.size;
      }

      // Create default field mappings
      const defaultMappings = {
        placeholders: AVAILABLE_PLACEHOLDERS.reduce((acc, item) => {
          acc[item.placeholder] = item.field;
          return acc;
        }, {})
      };

      // If setting as default, unset all other defaults first
      if (formData.setAsDefault) {
        await supabase
          .from('pdf_templates')
          .update({ is_default: false })
          .eq('template_type', 'job_listing');
      }

      // Save template metadata to database
      const templateRecord = {
        name: formData.name,
        description: formData.description,
        template_type: 'job_listing',
        template_format: formData.template_format,
        field_mappings: defaultMappings,
        is_default: formData.setAsDefault,
        created_by: session.user.id
      };

      // Add format-specific fields
      if (formData.template_format === 'pdf') {
        templateRecord.file_url = filePath;
        templateRecord.file_name = fileName;
        templateRecord.file_size = fileSize;
      } else {
        templateRecord.template_content = formData.template_content;
      }

      const { data: templateData, error: dbError } = await supabase
        .from('pdf_templates')
        .insert([templateRecord])
        .select()
        .single();

      if (dbError) throw dbError;

      setSuccess('Template created successfully');
      setUploadDialogOpen(false);
      setFormData({
        name: '',
        description: '',
        file: null,
        setAsDefault: true,
        template_format: 'html',
        template_content: ''
      });
      await fetchTemplates();
    } catch (err) {
      console.error('Error uploading template:', err);
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleSetDefault = async (templateId) => {
    try {
      // First, unset all defaults
      await supabase
        .from('pdf_templates')
        .update({ is_default: false })
        .eq('template_type', 'job_listing');

      // Then set the selected one as default
      const { error } = await supabase
        .from('pdf_templates')
        .update({ is_default: true })
        .eq('id', templateId);

      if (error) throw error;

      setSuccess('Default template updated');
      await fetchTemplates();
    } catch (err) {
      console.error('Error setting default:', err);
      setError('Failed to set default template');
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;

    try {
      // Delete from storage
      if (selectedTemplate.file_url) {
        const fileName = selectedTemplate.file_url.split('/').pop();
        await supabase.storage
          .from('pdf-templates')
          .remove([fileName]);
      }

      // Delete from database
      const { error } = await supabase
        .from('pdf_templates')
        .delete()
        .eq('id', selectedTemplate.id);

      if (error) throw error;

      setSuccess('Template deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedTemplate(null);
      await fetchTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      setError('Failed to delete template');
    }
  };

  const handleDownloadTemplate = async (template) => {
    try {
      const response = await fetch(template.file_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = template.file_name || 'template.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading template:', err);
      setError('Failed to download template');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">PDF Templates</h2>
          <p className="text-muted-foreground mt-1">
            Manage PDF templates for job listings
          </p>
        </div>
        <Button onClick={() => setUploadDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Upload Template
        </Button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      {/* Available Placeholders Info */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Info className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Available Placeholders</CardTitle>
          </div>
          <CardDescription>
            Use these placeholders in your PDF template. They will be automatically replaced with job data.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {AVAILABLE_PLACEHOLDERS.map((item) => (
              <div key={item.placeholder} className="space-y-1">
                <code className="text-xs bg-muted px-2 py-1 rounded">
                  {item.placeholder}
                </code>
                <p className="text-xs text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Templates List */}
      <Card>
        <CardHeader>
          <CardTitle>Uploaded Templates</CardTitle>
          <CardDescription>
            {templates.length} template{templates.length !== 1 ? 's' : ''} available
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading templates...</div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p>No templates uploaded yet</p>
              <p className="text-sm mt-1">Upload a PDF template to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Format</TableHead>
                  <TableHead>File Size</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell className="font-medium">{template.name}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {template.description || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={`text-xs ${
                          template.template_format === 'html'
                            ? 'bg-blue-50 text-blue-700 border-blue-200'
                            : 'bg-slate-50 text-slate-700 border-slate-200'
                        }`}
                      >
                        {template.template_format === 'html' ? 'HTML' : 'PDF'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {template.file_size ? `${(template.file_size / 1024).toFixed(1)} KB` : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {template.is_default && (
                          <Badge variant="default" className="text-xs">Default</Badge>
                        )}
                        {template.is_active ? (
                          <Badge variant="outline" className="text-xs text-green-600 border-green-200">
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">Inactive</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(template.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadTemplate(template)}
                          title="Download template"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        {!template.is_default && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSetDefault(template.id)}
                            title="Set as default"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedTemplate(template);
                            setDeleteDialogOpen(true);
                          }}
                          title="Delete template"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Template</DialogTitle>
            <DialogDescription>
              Create an HTML template (recommended) or upload a legacy PDF file with placeholders like {'{{job_title}}'}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Template Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Standard Job Listing Template"
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe this template..."
                rows={3}
              />
            </div>

            {/* Format Selection */}
            <div>
              <Label>Template Format *</Label>
              <div className="flex items-center gap-6 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="template_format"
                    value="html"
                    checked={formData.template_format === 'html'}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        template_format: 'html',
                        template_content: formData.template_content || DEFAULT_HTML_TEMPLATE,
                        file: null
                      });
                    }}
                    className="h-4 w-4"
                  />
                  <span className="text-sm font-medium">HTML Template (Recommended)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="template_format"
                    value="pdf"
                    checked={formData.template_format === 'pdf'}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        template_format: 'pdf',
                        template_content: ''
                      });
                    }}
                    className="h-4 w-4"
                  />
                  <span className="text-sm font-medium">PDF Template (Legacy)</span>
                </label>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {formData.template_format === 'html'
                  ? 'HTML templates allow full styling and inline placeholder replacement'
                  : 'PDF templates append data as a new page (placeholders are not replaced inline)'}
              </p>
            </div>

            {/* Conditional Inputs */}
            {formData.template_format === 'html' ? (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="htmlContent">HTML Content *</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    onClick={() => setFormData({ ...formData, template_content: DEFAULT_HTML_TEMPLATE })}
                  >
                    Load Default Template
                  </Button>
                </div>
                <Textarea
                  id="htmlContent"
                  value={formData.template_content}
                  onChange={(e) => setFormData({ ...formData, template_content: e.target.value })}
                  placeholder="Enter HTML content with placeholders..."
                  rows={15}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Use placeholders like {'{{job_title}}, {{client_name}}'}, etc. See available placeholders above.
                </p>
              </div>
            ) : (
              <div>
                <Label htmlFor="file">PDF File *</Label>
                <Input
                  id="file"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="cursor-pointer"
                />
                {formData.file && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Selected: {formData.file.name} ({(formData.file.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>
            )}

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="setAsDefault"
                checked={formData.setAsDefault}
                onChange={(e) => setFormData({ ...formData, setAsDefault: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="setAsDefault" className="cursor-pointer">
                Set as default template
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setUploadDialogOpen(false);
                setFormData({
                  name: '',
                  description: '',
                  file: null,
                  setAsDefault: true,
                  template_format: 'html',
                  template_content: ''
                });
              }}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={uploading}>
              {uploading ? 'Creating...' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Template</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedTemplate?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PDFTemplates;
